import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Alert,
  AlertDescription,
} from "@/components/ui";
import {
  ClipboardCheck,
  Plus,
  Search,
  Link2,
  Copy,
  Eye,
  Trash2,
  RefreshCw,
  Mail,
  Star,
  Clock,
  CheckCircle2,
  XCircle,
  MinusCircle,
  FileText,
  Share2,
  MessageSquare,
  Loader2,
  Send,
  Bell,
  Users,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  createProbationReview,
  getProbationReviews,
  getProbationReviewById,
  updateProbationReview,
  deleteProbationReview,
  sendProbationReview,
  buildPublicUrl,
} from "@/services/probationReviewService";
import employeeService from "@/services/employeeService";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  submitted: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
};

const STATUS_LABELS = {
  pending: "Pending",
  submitted: "Submitted",
  completed: "Completed",
};

const RATING_LABELS = {
  1: "Weak",
  2: "Below Average",
  3: "Average",
  4: "Good",
  5: "Strength",
};

const QUESTION_SECTIONS = [
  { label: "1. Quality of work", ratingKey: "q1_work_quality_rating", commentKey: "q1_work_quality_comment" },
  { label: "2. Quantity / On-time completion", ratingKey: "q2_quantity_timing_rating", commentKey: "q2_quantity_timing_comment" },
  { label: "3. Dependability", ratingKey: "q3_dependability_rating", commentKey: "q3_dependability_comment" },
  { label: "4. Team work", ratingKey: "q4_teamwork_rating", commentKey: "q4_teamwork_comment" },
  { label: "5. General attitude to work", ratingKey: "q5_attitude_rating", commentKey: "q5_attitude_comment" },
  { label: "6. Personality / Behaviour", ratingKey: "q6_personality_rating", commentKey: "q6_personality_comment" },
  { label: "7. Integrity", ratingKey: "q7_integrity_rating", commentKey: "q7_integrity_comment" },
  { label: "8. Hygiene / Dressing", ratingKey: "q8_hygiene_rating", commentKey: "q8_hygiene_comment" },
  { label: "9. Fit into the team", ratingKey: "q9_fit_rating", commentKey: "q9_fit_comment" },
];

const recommendationInfo = (value) => {
  if (value === "yes")
    return { label: "Yes, recommend for confirmation", icon: CheckCircle2, cls: "text-green-600" };
  if (value === "no")
    return { label: "No, not recommended for confirmation", icon: XCircle, cls: "text-red-600" };
  if (value === "not_sure")
    return { label: "Not sure", icon: MinusCircle, cls: "text-amber-600" };
  return { label: "—", icon: MinusCircle, cls: "text-gray-400" };
};

const ProbationReviews = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    employee_name: "",
    job_title: "",
    department: "",
    review_period: "3-Month Probation Review",
  });

  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewDetail, setViewDetail] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [copiedIndex, setCopiedIndex] = useState(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await getProbationReviews(params);
      const data = res?.data;
      setReviews(data?.reviews || []);
    } catch (err) {
      console.error("Fetch probation reviews error:", err);
      toast.error(err.response?.data?.message || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const timeout = setTimeout(fetchReviews, search ? 400 : 0);
    return () => clearTimeout(timeout);
  }, [fetchReviews, search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await createProbationReview(form);
      toast.success("Probation review created");
      setShowCreateDialog(false);
      setForm({
        employee_name: "",
        job_title: "",
        department: "",
        review_period: "Probation Review",
      });
      await fetchReviews();
      if (res?.data?.publicUrl) {
        try {
          await navigator.clipboard.writeText(res.data.publicUrl);
          toast.success("Share link copied to clipboard");
        } catch (clipErr) {
          toast(`Share link: ${res.data.publicUrl}`, { duration: 8000 });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create review");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (review, index) => {
    const url = buildPublicUrl(review.public_link);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
      toast.success("Share link copied!");
    } catch (err) {
      toast.error("Copy failed - please copy manually");
    }
  };

  const openView = async (id) => {
    setShowViewDialog(true);
    setViewLoading(true);
    setViewDetail(null);
    try {
      const res = await getProbationReviewById(id);
      const detail = res?.data?.review;
      if (!detail) throw new Error("Review not found");
      detail.publicUrl =
        res?.data?.publicUrl || buildPublicUrl(detail?.public_link);
      setViewDetail(detail);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load review detail");
      setShowViewDialog(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleRegenerate = async (review) => {
    try {
      const res = await updateProbationReview(review.id, { regenerate_link: true });
      toast.success("New share link generated");
      await fetchReviews();
      if (res?.data?.publicUrl) {
        try {
          await navigator.clipboard.writeText(res.data.publicUrl);
          toast.success("New link copied to clipboard");
        } catch {
          /* ignore clipboard errors */
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to regenerate link");
    }
  };

  const handleDelete = async (review) => {
    if (!window.confirm(`Delete probation review for ${review.employee_name}?`)) return;
    try {
      await deleteProbationReview(review.id);
      toast.success("Review deleted");
      await fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete review");
    }
  };

  const openShare = async (review) => {
    setShareTarget(review);
    setEmailInput("");
    setSelectedUserIds([]);
    setShowShareDialog(true);
    if (employees.length === 0) {
      setEmployeesLoading(true);
      try {
        const res = await employeeService.getEmployees({ limit: 200 });
        const list =
          res?.data?.employees || res?.data?.data?.employees || [];
        setEmployees(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Fetch employees for share:", err);
        toast.error("Could not load employees for notification");
      } finally {
        setEmployeesLoading(false);
      }
    }
  };

  const shareWhatsApp = (review) => {
    const url = buildPublicUrl(review.public_link);
    const text = `Please complete the probation review for ${review.employee_name}${review.job_title ? ` (${review.job_title})` : ""}: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const shareEmailClient = (review) => {
    const url = buildPublicUrl(review.public_link);
    const subject = `Probation Review Request: ${review.employee_name}`;
    const body = `Hello,\n\nYou have been requested to complete a probation review for ${review.employee_name}${review.job_title ? ` (${review.job_title})` : ""}.\n\nPlease use this link (no login required):\n${url}\n\nThank you.`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSendEmail = async () => {
    if (!emailInput.trim()) {
      toast.error("Enter at least one recipient email");
      return;
    }
    setSending(true);
    try {
      const res = await sendProbationReview(shareTarget.id, {
        method: "email",
        email: emailInput,
      });
      if (res.success) toast.success(res.message);
      else toast.error(res.message || "Email could not be sent");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const toggleUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id],
    );
  };

  const handleSendNotification = async () => {
    if (selectedUserIds.length === 0) {
      toast.error("Select at least one recipient");
      return;
    }
    setSending(true);
    try {
      const res = await sendProbationReview(shareTarget.id, {
        method: "notification",
        user_ids: selectedUserIds,
      });
      if (res.success) toast.success(res.message);
      else toast.error(res.message || "Notification could not be sent");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    const d = new Date(date);
    return isNaN(d) ? "—" : d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const sectionAverage = (submissions, ratingKey) => {
    const values = submissions
      .map((s) => parseInt(s[ratingKey], 10))
      .filter((v) => v >= 1 && v <= 5);
    if (values.length === 0) return null;
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  const averageRating = (r) => {
    if (r.average_rating !== undefined && r.average_rating !== null) {
      const val = parseFloat(r.average_rating);
      if (!isNaN(val) && val > 0) return val.toFixed(1);
    }
    const keys = QUESTION_SECTIONS.map((s) => s.ratingKey);
    const values = keys
      .map((k) => parseInt(r[k], 10))
      .filter((v) => v >= 1 && v <= 5);
    if (values.length === 0) return null;
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-blue-600" />
            Probation Reviews
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Generate shareable review links for employees on probation. Links can be
            shared with anyone - no login required.
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" /> New Review
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search by employee, title or department..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border rounded-md bg-white text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="submitted">Submitted</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p>No probation reviews found. Click "New Review" to create one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Responses</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Last Response</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.map((review, idx) => {
                    const avg = averageRating(review);
                    return (
                      <TableRow key={review.id}>
                        <TableCell>
                          <div className="font-medium text-gray-900">
                            {review.employee_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {[review.job_title, review.department]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {review.review_period || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_STYLES[review.status] || STATUS_STYLES.pending}>
                            {STATUS_LABELS[review.status] || review.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {review.submission_count > 0 ? (
                            <span className="flex items-center gap-1 text-gray-900 font-medium">
                              <Users className="h-4 w-4 text-blue-500" />
                              {review.submission_count}{" "}
                              {review.submission_count === 1 ? "response" : "responses"}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {avg ? (
                            <span className="flex items-center gap-1 text-gray-900 font-medium">
                              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                              {avg} / 5
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(review.submission_date)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Share link"
                              onClick={() => openShare(review)}
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Copy share link"
                              onClick={() => copyLink(review, idx)}
                            >
                              {copiedIndex === idx ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="View responses"
                              onClick={() => openView(review.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Regenerate link"
                              onClick={() => handleRegenerate(review)}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Delete"
                              onClick={() => handleDelete(review)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Probation Review</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Employee Name *</Label>
              <Input
                required
                value={form.employee_name}
                onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                placeholder="e.g. John Doe"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Job Title</Label>
                <Input
                  value={form.job_title}
                  onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                  placeholder="e.g. Sales Officer"
                />
              </div>
              <div>
                <Label>Department</Label>
                <Input
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  placeholder="e.g. Sales"
                />
              </div>
            </div>
            <div>
              <Label>Review Period</Label>
              <Input
                value={form.review_period}
                onChange={(e) => setForm({ ...form, review_period: e.target.value })}
                placeholder="e.g. 3-Month Probation Review"
              />
            </div>
            <Alert>
              <AlertDescription className="flex items-start gap-2 text-sm">
                <Link2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                A unique shareable link will be generated. Anyone with the link can
                submit the review without logging into the app.
              </AlertDescription>
            </Alert>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? "Creating..." : "Create & Generate Link"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Probation Review</DialogTitle>
          </DialogHeader>
          {shareTarget && (
            <div className="space-y-5">
              <p className="text-sm text-gray-600">
                Share this link for{" "}
                <span className="font-semibold text-gray-900">
                  {shareTarget.employee_name}
                </span>
                {shareTarget.job_title
                  ? ` (${shareTarget.job_title})`
                  : ""}
                . Anyone with the link can complete the review -{" "}
                <strong>no login or registration required</strong>.
              </p>

              {/* Link + copy */}
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={buildPublicUrl(shareTarget.public_link)}
                  className="text-xs"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        buildPublicUrl(shareTarget.public_link),
                      );
                      toast.success("Link copied");
                    } catch {
                      toast.error("Copy failed");
                    }
                  }}
                >
                  <Copy className="h-4 w-4 mr-1" /> Copy
                </Button>
              </div>

              {/* External share */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Share via WhatsApp / Email (external)
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="text-green-700 border-green-300 hover:bg-green-50"
                    onClick={() => shareWhatsApp(shareTarget)}
                  >
                    <MessageSquare className="h-4 w-4 mr-1" /> WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    className="text-blue-700 border-blue-300 hover:bg-blue-50"
                    onClick={() => shareEmailClient(shareTarget)}
                  >
                    <Mail className="h-4 w-4 mr-1" /> Email
                  </Button>
                </div>
              </div>

              <div className="border-t pt-4 space-y-5">
                <p className="text-sm font-semibold text-gray-700">
                  Send via app
                </p>

                {/* Send email */}
                <div>
                  <Label>Send link by email</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="text"
                      placeholder="email@company.com (comma-separated for multiple)"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                    />
                    <Button
                      size="sm"
                      disabled={sending}
                      onClick={handleSendEmail}
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      Send
                    </Button>
                  </div>
                </div>

                {/* Send in-app notification */}
                <div>
                  <Label>Send in-app notification to</Label>
                  <div className="mt-1 border rounded-md max-h-44 overflow-y-auto divide-y divide-gray-100">
                    {employeesLoading ? (
                      <div className="flex justify-center py-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      </div>
                    ) : employees.length === 0 ? (
                      <p className="text-sm text-gray-400 py-4 text-center">
                        No employees available
                      </p>
                    ) : (
                      employees.map((emp) => {
                        const empId = emp.user_id || emp.User?.id;
                        if (!empId) return null;
                        const checked = selectedUserIds.includes(empId);
                        const name =
                          emp.first_name || emp.User?.first_name
                            ? `${emp.first_name || emp.User?.first_name} ${emp.last_name || emp.User?.last_name || ""}`.trim()
                            : emp.employee_id;
                        return (
                          <button
                            key={empId}
                            type="button"
                            onClick={() => toggleUser(empId)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                              checked ? "bg-blue-50" : "hover:bg-gray-50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              readOnly
                              checked={checked}
                              className="accent-blue-600"
                            />
                            <span className="flex-1 font-medium text-gray-800">
                              {name}
                            </span>
                            <span className="text-xs text-gray-500 truncate max-w-[140px]">
                              {emp.email || emp.User?.email || ""}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="mt-2"
                    disabled={sending}
                    onClick={handleSendNotification}
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Bell className="h-4 w-4" />
                    )}
                    Send to {selectedUserIds.length} recipient
                    {selectedUserIds.length === 1 ? "" : "s"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Detail Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Probation Review Details</DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : viewDetail ? (
            <div className="space-y-6">
              {/* Employee + share link */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-500">Employee</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {viewDetail.employee_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {[viewDetail.job_title, viewDetail.department]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                      {viewDetail.review_period ? ` · ${viewDetail.review_period}` : ""}
                    </p>
                  </div>
                  <Badge className={STATUS_STYLES[viewDetail.status] || STATUS_STYLES.pending}>
                    {STATUS_LABELS[viewDetail.status] || viewDetail.status}
                  </Badge>
                </div>

                {/* Share link */}
                {viewDetail.publicUrl && (
                  <div className="mt-3 flex items-center gap-2">
                    <Input readOnly value={viewDetail.publicUrl} className="text-xs" />
                    <Button size="sm" onClick={() => navigate(viewDetail.publicUrl)}>
                      <Link2 className="h-4 w-4 mr-1" /> Open
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(viewDetail.publicUrl);
                          toast.success("Link copied");
                        } catch {
                          toast.error("Copy failed");
                        }
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" /> Copy
                    </Button>
                  </div>
                )}
              </div>

              {/* Submissions summary */}
              {viewDetail.submissions?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-semibold text-blue-900 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {viewDetail.submissions.length}{" "}
                    {viewDetail.submissions.length === 1 ? "response" : "responses"}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 text-sm">
                    {QUESTION_SECTIONS.map((section) => {
                      const avg = sectionAverage(viewDetail.submissions, section.ratingKey);
                      return (
                        <div
                          key={section.ratingKey}
                          className="bg-white rounded-md border border-blue-100 px-3 py-2"
                        >
                          <p className="text-xs text-gray-500">{section.label.replace(/^\d+\.\s*/, "")}</p>
                          <p className="font-semibold text-blue-900">
                            {avg != null ? (
                              <span className="flex items-center gap-1">
                                <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                                {avg} / 5
                              </span>
                            ) : (
                              "—"
                            )}
                          </p>
                        </div>
                      );
                    })}
                    <div className="bg-white rounded-md border border-blue-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Recommendation</p>
                      {(() => {
                        const tally = { yes: 0, no: 0, not_sure: 0 };
                        viewDetail.submissions.forEach((s) => {
                          if (tally[s.q10_recommend] !== undefined) tally[s.q10_recommend]++;
                        });
                        return (
                          <p className="text-xs font-medium text-gray-700 mt-1 space-y-0.5">
                            {tally.yes > 0 && (
                              <span className="flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Yes: {tally.yes}
                              </span>
                            )}
                            {tally.no > 0 && (
                              <span className="flex items-center gap-1">
                                <XCircle className="h-3.5 w-3.5 text-red-600" /> No: {tally.no}
                              </span>
                            )}
                            {tally.not_sure > 0 && (
                              <span className="flex items-center gap-1">
                                <MinusCircle className="h-3.5 w-3.5 text-amber-600" /> Not sure: {tally.not_sure}
                              </span>
                            )}
                            {tally.yes === 0 && tally.no === 0 && tally.not_sure === 0 && "—"}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Each reviewer's submission */}
              {viewDetail.submissions?.length > 0 ? (
                viewDetail.submissions.map((sub, subIdx) => (
                  <div key={sub.id} className="border rounded-lg p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <span className="bg-blue-100 text-blue-700 rounded-full h-7 w-7 flex items-center justify-center text-sm font-bold">
                          {subIdx + 1}
                        </span>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {sub.reviewer_name || "Anonymous"}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {sub.reviewer_email || "—"}
                            {sub.reviewer_relationship
                              ? ` · ${sub.reviewer_relationship}`
                              : ""}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(sub.submission_date)}
                      </p>
                    </div>

                    <div className="space-y-2">
                      {QUESTION_SECTIONS.map((section) => {
                        const rating = parseInt(sub[section.ratingKey], 10);
                        const comment = sub[section.commentKey];
                        return (
                          <div
                            key={section.ratingKey}
                            className="rounded-md bg-gray-50 px-3 py-2"
                          >
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <p className="text-sm font-medium text-gray-800">
                                {section.label}
                              </p>
                              {rating >= 1 && rating <= 5 ? (
                                <span className="flex items-center gap-1 text-sm font-semibold text-amber-700">
                                  <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                                  {rating} / 5 · {RATING_LABELS[rating]}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400">Not rated</span>
                              )}
                            </div>
                            {comment ? (
                              <p className="mt-1 text-sm text-gray-600">{comment}</p>
                            ) : (
                              <p className="mt-1 text-sm text-gray-400 italic">No comment</p>
                            )}
                          </div>
                        );
                      })}

                      <div className="rounded-md bg-gray-50 px-3 py-2">
                        <p className="text-sm font-medium text-gray-800">
                          10. Recommendation for confirmation
                        </p>
                        {sub.q10_recommend ? (
                          (() => {
                            const rec = recommendationInfo(sub.q10_recommend);
                            return (
                              <p className="mt-1 flex items-center gap-2 text-sm font-medium">
                                <rec.icon className={`h-4 w-4 ${rec.cls}`} />
                                <span className={rec.cls}>{rec.label}</span>
                              </p>
                            );
                          })()
                        ) : (
                          <p className="text-sm text-gray-400 mt-1">Not answered</p>
                        )}
                        {sub.q10_comment && (
                          <p className="mt-2 text-sm text-gray-600">{sub.q10_comment}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="border rounded-lg p-4 text-center text-gray-500">
                  No responses yet. Share the link to collect reviews.
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500">No data.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProbationReviews;